import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, ChevronRight } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { formatCents, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import { COUNTERTOP_JOB_STATUS_BADGE } from '@/lib/badges';
import type { CountertopJobListItem } from '@/lib/api-types';
import type { CountertopJobStatus } from '@billflow/shared';

// ─── Status tabs ─────────────────────────────────────────────────────────────

type StatusFilter = 'all' | CountertopJobStatus;

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Inquiry', value: 'inquiry' },
  { label: 'Quoted', value: 'quoted' },
  { label: 'Advance Paid', value: 'advance_paid' },
  { label: 'Measured', value: 'measured' },
  { label: 'Installing', value: 'installing' },
  { label: 'Installed', value: 'installed' },
  { label: 'Cancelled', value: 'cancelled' },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CountertopJobsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('all');

  const {
    rows: jobs,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<CountertopJobListItem>({
    endpoint: '/api/countertop-jobs',
    entityKey: 'countertop_jobs',
    defaultSort: 'created',
    defaultDir: 'desc',
    extraParams: {
      status: status !== 'all' ? status : undefined,
    },
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Countertop Jobs</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/countertop-jobs"
            params={{
              status: status !== 'all' ? status : undefined,
              q: q || undefined,
            }}
          />
          <Button size="sm" onClick={() => navigate('/countertop-jobs/new')}>
            <Plus className="h-4 w-4 mr-1.5" />
            New job
          </Button>
        </div>
      </div>

      {/* Filters — search + status pills */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Job # or customer…"
            className="pl-9 h-9"
          />
        </div>

        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Number"
                sortKey="number"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-32"
              />
              <SortableTh
                label="Customer"
                sortKey="customer"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
              />
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground w-32">
                Approx quote
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                Supplier
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-28 hidden sm:table-cell">
                Install date
              </th>
              <SortableTh
                label="Created"
                sortKey="created"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28 hidden lg:table-cell"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && jobs.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  No countertop jobs found.
                </td>
              </tr>
            )}
            {jobs.map((job) => {
              const badge = COUNTERTOP_JOB_STATUS_BADGE[job.status];
              return (
                <tr key={job.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/countertop-jobs/${job.id}`}
                      className="font-mono text-xs hover:underline underline-offset-4"
                    >
                      {job.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/customers/${job.customer_id}`}
                      className="hover:underline underline-offset-4"
                    >
                      {job.customer_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {job.approx_quote != null ? (
                      formatCents(job.approx_quote)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                    {job.supplier_name ?? <span className="text-muted-foreground/60">—</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell whitespace-nowrap">
                    {job.install_date ? (
                      formatDate(job.install_date)
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell whitespace-nowrap">
                    {formatDate(job.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <Link to={`/countertop-jobs/${job.id}`}>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                          View
                          <ChevronRight className="h-3 w-3 ml-0.5" />
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}
    </div>
  );
}
